import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CustomerRead, SortDirection, FormStatus } from '@/types';
import { logger } from '@/utils/secureLogger';
import { trackCreate, trackDelete, trackEdit, trackRestore } from '@/services/analytics';
import { getCustomerDoc, getCustomerDocs, addCustomerDoc, setCustomerDoc, deleteCustomerDoc, restoreCustomerDoc } from '@/services/firebaseApi';

export enum CustomersSortBy {
    ID = 'customerId',
    NAME = 'name',
    EMAIL = 'email',
    PHONE = 'phone',
}

interface UseCustomersProps {
    storageId?: string;
    customerId?: string;
}

const useCustomers = ({ storageId, customerId }: UseCustomersProps) => {
    const queryClient = useQueryClient();
    
    // Filter states
    const [sortBy, setSortBy] = useState(CustomersSortBy.NAME);
    const [sortDirection, setSortDirection] = useState(SortDirection.ASCENDING);
    const [filterQuery, setFilterQuery] = useState<string>('');
    const [displayDeleted, setDisplayDeleted] = useState<boolean>(false);

    // All customers query
    const {
        data: allCustomers,
        isLoading: customersLoading,
        error: customersError,
        refetch: refetchCustomers
    } = useQuery({
        queryKey: ['customers', storageId, displayDeleted],
        queryFn: () => getCustomerDocs(storageId!, displayDeleted),
        enabled: !!(storageId && !customerId),
        staleTime: 5 * 60 * 1000, // Customer data doesn't change often
        gcTime: 15 * 60 * 1000,
    });

    // Single customer query
    const {
        data: customer,
        isLoading: customerLoading,
        error: customerError,
        refetch: refetchCustomer
    } = useQuery({
        queryKey: ['customer', storageId, customerId],
        queryFn: () => getCustomerDoc(storageId!, customerId!),
        enabled: !!(storageId && customerId),
        staleTime: 5 * 60 * 1000,
    });

    // Apply client-side filtering and sorting
    const filteredCustomers = useMemo(() => {
        if (!allCustomers) return [];
        
        let filtered = [...allCustomers];

        // Apply text filter
        if (filterQuery) {
            const lower = filterQuery.toLowerCase();
            filtered = filtered.filter((customer) =>
                customer.customerId.toString().includes(lower) ||
                customer.name.toLowerCase().includes(lower) ||
                (customer.contactInformation.email && customer.contactInformation.email.toLowerCase().includes(lower)) ||
                (customer.contactInformation.phone && customer.contactInformation.phone.toLowerCase().includes(lower)) ||
                (customer.notes && customer.notes.toLowerCase().includes(lower))
            );
        }

        // Note: displayDeleted filtering is now handled server-side via the includeDeleted parameter

        return filtered;
    }, [allCustomers, filterQuery]);

    // Mutations
    const createCustomerMutation = useMutation({
        mutationFn: addCustomerDoc,
        onSuccess: (_, variables) => {
            trackCreate('customer');
            logger.info('Customer created successfully');
            queryClient.invalidateQueries({ queryKey: ['customers', variables.ids.storageId, true] });
            queryClient.invalidateQueries({ queryKey: ['customers', variables.ids.storageId, false] });
            queryClient.invalidateQueries({ queryKey: ['storage', variables.ids.storageId] });
            queryClient.invalidateQueries({ queryKey: ['storages'] });
        },
        onError: (error) => {
            logger.error('Failed to create customer:', error, 'useCustomers');
        }
    });

    const updateCustomerMutation = useMutation({
        mutationFn: setCustomerDoc,
        onSuccess: (_, variables) => {
            trackEdit('customer');
            logger.info('Customer updated successfully:', variables.ids.customerId);
            queryClient.invalidateQueries({ queryKey: ['customers', variables.ids.storageId, true] });
            queryClient.invalidateQueries({ queryKey: ['customers', variables.ids.storageId, false] });
            queryClient.invalidateQueries({ queryKey: ['customer', variables.ids.storageId, variables.ids.customerId] });
        },
        onError: (error) => {
            logger.error('Failed to update customer:', error, 'useCustomers');
        }
    });

    const deleteCustomerMutation = useMutation({
        mutationFn: deleteCustomerDoc,
        onSuccess: (_, variables) => {
            trackDelete('customer');
            logger.info('Customer deleted successfully:', variables.customerId);
            queryClient.invalidateQueries({ queryKey: ['customers', variables.storageId, true] });
            queryClient.invalidateQueries({ queryKey: ['customers', variables.storageId, false] });
            queryClient.invalidateQueries({ queryKey: ['customer', variables.storageId, variables.customerId] });
        },
        onError: (error) => {
            logger.error('Failed to delete customer:', error, 'useCustomers');
        }
    });

    const restoreCustomerMutation = useMutation({
        mutationFn: restoreCustomerDoc,
        onSuccess: (_, variables) => {
            trackRestore('customer');
            logger.info('Customer restored successfully:', variables.customerId);
            queryClient.invalidateQueries({ queryKey: ['customers', variables.storageId, true] });
            queryClient.invalidateQueries({ queryKey: ['customers', variables.storageId, false] });
            queryClient.invalidateQueries({ queryKey: ['customer', variables.storageId, variables.customerId] });
        },
        onError: (error) => {
            logger.error('Failed to restore customer:', error, 'useCustomers');
        }
    });

    // Helper function to determine status
    const getStatus = (loading: boolean, error: any): FormStatus => {
        if (loading) return FormStatus.LOADING;
        if (error) return FormStatus.ERROR;
        return FormStatus.SUCCESS;
    };

    // Backward compatible functions
    const reloadCustomers = () => {
        if (customerId) {
            refetchCustomer();
        } else {
            refetchCustomers();
        }
    };

    return {
        // Single customer
        customer: customer || null,
        customerStatus: getStatus(customerLoading, customerError),
        
        // Multiple customers
        customers: allCustomers || [],
        filteredCustomers,
        customersStatus: getStatus(customersLoading, customersError),
        
        // Filtering and sorting
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        filterQuery,
        setFilterQuery,
        displayDeleted,
        setDisplayDeleted,
        
        // Actions
        reloadCustomers,
        
        // Mutations (async only)
        createCustomerAsync: createCustomerMutation.mutateAsync,
        updateCustomerAsync: updateCustomerMutation.mutateAsync,
        deleteCustomerAsync: deleteCustomerMutation.mutateAsync,
        restoreCustomerAsync: restoreCustomerMutation.mutateAsync,
        isCreating: createCustomerMutation.isPending,
        isUpdating: updateCustomerMutation.isPending,
        isDeleting: deleteCustomerMutation.isPending,
        isRestoring: restoreCustomerMutation.isPending,
        
        // Refresh functions
        refreshCustomers: refetchCustomers,
        refreshCustomer: refetchCustomer,
    };
};

export default useCustomers;
